import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  ChevronLeft,
  CheckCircle2,
  Upload,
  X,
} from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

interface EligibleOwner {
  id: string;
  fullName: string;
  email: string;
}

interface PayrollImportItem {
  id: string;
  pageNumber: number;
  detectedNif: string | null;
  detectedMonth: number | null;
  detectedYear: number | null;
  matchedUserId: string | null;
  matchedUserName: string | null;
}

interface PayrollImportJob {
  id: string;
  filename: string;
  totalPages: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  createdAt: string;
  confirmedAt: string | null;
  items: PayrollImportItem[];
}

interface ItemOverride {
  ownerUserId: string;
  periodMonth: number | null;
  periodYear: number | null;
  skipped: boolean;
}

const MONTH_LONG = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function AdminPayrollImport() {
  const [step, setStep] = useState<'upload' | 'review' | 'done'>('upload');
  const [job, setJob] = useState<PayrollImportJob | null>(null);
  const [overrides, setOverrides] = useState<Map<number, ItemOverride>>(new Map());
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const owners = useQuery({
    queryKey: ['admin', 'documents', 'eligible-owners'],
    queryFn: () => api<EligibleOwner[]>('/admin/documents/eligible-owners'),
  });

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-3">
        <Link
          to="/admin/documentos"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-icaria-600 transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Volver a Documentos
        </Link>
      </div>

      <StepNav step={step} />

      {step === 'upload' ? (
        <UploadStep
          onUploaded={(j) => {
            setJob(j);
            const next = new Map<number, ItemOverride>();
            for (const it of j.items) {
              next.set(it.pageNumber, {
                ownerUserId: it.matchedUserId ?? '',
                periodMonth: it.detectedMonth,
                periodYear: it.detectedYear,
                skipped: false,
              });
            }
            setOverrides(next);
            setStep('review');
          }}
        />
      ) : null}

      {step === 'review' && job ? (
        <ReviewStep
          job={job}
          owners={owners.data ?? []}
          overrides={overrides}
          setOverrides={setOverrides}
          onCancelled={() => {
            setJob(null);
            setOverrides(new Map());
            setStep('upload');
          }}
          onConfirmed={(r) => {
            setResult(r);
            setStep('done');
          }}
        />
      ) : null}

      {step === 'done' && result ? (
        <DoneStep result={result} />
      ) : null}
    </div>
  );
}

function StepNav({ step }: { step: 'upload' | 'review' | 'done' }) {
  const steps = [
    { id: 'upload', label: 'Subir PDF' },
    { id: 'review', label: 'Revisar y emparejar' },
    { id: 'done', label: 'Confirmar' },
  ] as const;
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <ol className="flex items-center gap-2 mb-5 font-mono text-[11px] uppercase tracking-cap">
      {steps.map((s, i) => (
        <li key={s.id} className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-semibold',
              i < idx
                ? 'bg-success text-paper'
                : i === idx
                  ? 'bg-icaria-600 text-paper'
                  : 'bg-paper border border-line-strong text-muted-foreground',
            )}
          >
            {i + 1}
          </span>
          <span
            className={cn(
              i === idx ? 'text-foreground font-semibold' : 'text-muted-foreground',
            )}
          >
            {s.label}
          </span>
          {i < steps.length - 1 ? (
            <span className="h-px w-6 bg-line-strong inline-block" />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function UploadStep({ onUploaded }: { onUploaded: (job: PayrollImportJob) => void }) {
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Selecciona un PDF');
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/admin/payroll-imports`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const data: { message?: string | string[] } = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.message) ? data.message[0] : data.message;
        throw new Error(msg ?? `Error ${res.status}`);
      }
      return (await res.json()) as PayrollImportJob;
    },
    onSuccess: onUploaded,
  });

  return (
    <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
      <header className="px-7 py-5 border-b border-border">
        <h2 className="text-lg font-semibold tracking-tightish">Importar nóminas en lote</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Sube un PDF con varias nóminas (una por página). El sistema detectará el NIF en cada
          página y propondrá el empleado de destino.
        </p>
      </header>

      <div className="px-7 py-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payroll-pdf">
            PDF de nóminas <span className="text-destructive">*</span>
          </Label>
          <input
            id="payroll-pdf"
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-[13px] file:mr-3 file:h-9 file:px-3 file:rounded-sm file:border file:border-line-strong file:bg-paper file:font-medium file:text-foreground hover:file:bg-card"
          />
          {file ? (
            <p className="font-mono text-[11px] text-muted-foreground">
              {file.name} · {formatSize(file.size)}
            </p>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            PDF nativo (no escaneado), máx 50 MB. Una nómina por página.
          </p>
        </div>

        {upload.error ? (
          <p role="alert" className="text-sm text-destructive">
            {(upload.error as Error).message}
          </p>
        ) : null}

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => upload.mutate()}
            disabled={!file || upload.isPending}
          >
            <Upload className="h-3.5 w-3.5" />
            {upload.isPending ? 'Analizando PDF…' : 'Subir y analizar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({
  job,
  owners,
  overrides,
  setOverrides,
  onCancelled,
  onConfirmed,
}: {
  job: PayrollImportJob;
  owners: EligibleOwner[];
  overrides: Map<number, ItemOverride>;
  setOverrides: (m: Map<number, ItemOverride>) => void;
  onCancelled: () => void;
  onConfirmed: (r: { created: number; skipped: number }) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const matched = job.items.filter((it) => it.matchedUserId).length;
  const unmatched = job.items.length - matched;

  function updateOverride(pageNumber: number, patch: Partial<ItemOverride>) {
    const next = new Map(overrides);
    const current = next.get(pageNumber) ?? {
      ownerUserId: '',
      periodMonth: null,
      periodYear: null,
      skipped: false,
    };
    next.set(pageNumber, { ...current, ...patch });
    setOverrides(next);
  }

  const confirm = useMutation({
    mutationFn: async () => {
      const items = job.items.map((it) => {
        const ov = overrides.get(it.pageNumber);
        return {
          pageNumber: it.pageNumber,
          ownerUserId: ov?.skipped ? undefined : ov?.ownerUserId || undefined,
          periodMonth: ov?.periodMonth ?? undefined,
          periodYear: ov?.periodYear ?? undefined,
          skipped: ov?.skipped ?? false,
        };
      });
      return api<{ jobId: string; created: number; skipped: number }>(
        `/admin/payroll-imports/${job.id}/confirm`,
        { method: 'POST', body: JSON.stringify({ items }) },
      );
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
      onConfirmed({ created: r.created, skipped: r.skipped });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api<void>(`/admin/payroll-imports/${job.id}`, { method: 'DELETE' }),
    onSuccess: onCancelled,
  });

  const downloadZip = useMutation({
    mutationFn: async () => {
      const items = job.items.map((it) => {
        const ov = overrides.get(it.pageNumber);
        return {
          pageNumber: it.pageNumber,
          ownerUserId: ov?.skipped ? undefined : ov?.ownerUserId || undefined,
          periodMonth: ov?.periodMonth ?? undefined,
          periodYear: ov?.periodYear ?? undefined,
          skipped: ov?.skipped ?? false,
        };
      });
      const res = await fetch(`${API_BASE_URL}/admin/payroll-imports/${job.id}/preview-zip`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const data: { message?: string | string[] } = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.message) ? data.message[0] : data.message;
        throw new Error(msg ?? `Error ${res.status}`);
      }
      const blob = await res.blob();
      // Filename del header Content-Disposition (UTF-8 encoded por el backend)
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i) ?? cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? decodeURIComponent(m[1]) : 'nominas_borrador.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : 'Error al generar el ZIP');
    },
  });

  // Validación previa: si hay items sin owner y sin skipped, no se puede confirmar.
  const blockers = job.items.filter((it) => {
    const ov = overrides.get(it.pageNumber);
    if (!ov || ov.skipped) return false;
    return !ov.ownerUserId;
  });
  const canConfirm = blockers.length === 0 && !confirm.isPending;

  return (
    <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
      <header className="px-7 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold tracking-tightish">Revisar emparejamiento</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            {job.filename} · {job.totalPages} páginas ·{' '}
            <span className="text-success font-medium">{matched} emparejadas</span>
            {unmatched > 0 ? (
              <>
                {' '}·{' '}
                <span className="text-warning font-medium">
                  {unmatched} requieren asignación manual
                </span>
              </>
            ) : null}
          </p>
        </div>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr>
              <Th>Pág</Th>
              <Th>NIF detectado</Th>
              <Th>Empleado</Th>
              <Th align="center">Mes</Th>
              <Th align="center">Año</Th>
              <Th align="center">Descartar</Th>
            </tr>
          </thead>
          <tbody>
            {job.items.map((it) => {
              const ov = overrides.get(it.pageNumber);
              const isBlocker = blockers.some((b) => b.pageNumber === it.pageNumber);
              return (
                <tr
                  key={it.id}
                  className={cn(
                    'border-t border-border first:border-t-0 transition-colors',
                    ov?.skipped ? 'opacity-50' : isBlocker ? 'bg-warning/5' : '',
                  )}
                >
                  <Td>
                    <span className="font-mono font-semibold">{it.pageNumber}</span>
                  </Td>
                  <Td>
                    {it.detectedNif ? (
                      <span className="font-mono text-[12px]">{it.detectedNif}</span>
                    ) : (
                      <Badge tone="amber">No detectado</Badge>
                    )}
                  </Td>
                  <Td>
                    <select
                      value={ov?.ownerUserId ?? ''}
                      onChange={(e) =>
                        updateOverride(it.pageNumber, { ownerUserId: e.target.value })
                      }
                      disabled={ov?.skipped}
                      className="h-9 min-w-[240px] rounded-sm border border-input bg-card px-2.5 text-[13px] focus-visible:border-icaria-600 focus-visible:outline-none disabled:opacity-50"
                    >
                      <option value="">— Selecciona empleado —</option>
                      {owners.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.fullName}
                        </option>
                      ))}
                    </select>
                    {!it.matchedUserId && ov?.ownerUserId && !ov?.skipped ? (
                      <p className="text-[10.5px] font-mono uppercase tracking-cap text-warning mt-1">
                        asignado manualmente
                      </p>
                    ) : null}
                  </Td>
                  <Td align="center">
                    <select
                      value={ov?.periodMonth ?? ''}
                      onChange={(e) =>
                        updateOverride(it.pageNumber, {
                          periodMonth: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      disabled={ov?.skipped}
                      className="h-9 rounded-sm border border-input bg-card px-2 text-[13px] focus-visible:border-icaria-600 focus-visible:outline-none disabled:opacity-50"
                    >
                      <option value="">—</option>
                      {MONTH_LONG.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </Td>
                  <Td align="center">
                    <Input
                      type="number"
                      min={2000}
                      max={2100}
                      value={ov?.periodYear ?? ''}
                      onChange={(e) =>
                        updateOverride(it.pageNumber, {
                          periodYear: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      disabled={ov?.skipped}
                      className="h-9 w-20 text-center"
                    />
                  </Td>
                  <Td align="center">
                    <input
                      type="checkbox"
                      checked={ov?.skipped ?? false}
                      onChange={(e) =>
                        updateOverride(it.pageNumber, { skipped: e.target.checked })
                      }
                      className="h-4 w-4 accent-icaria-600 cursor-pointer"
                    />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {blockers.length > 0 ? (
        <div className="px-7 py-3 border-t border-border bg-warning/5 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-warning">
            {blockers.length} página{blockers.length === 1 ? '' : 's'} sin empleado asignado. Asigna
            o márcalas como descartadas para poder confirmar.
          </p>
        </div>
      ) : null}

      {confirm.error ? (
        <p role="alert" className="px-7 py-3 text-sm text-destructive border-t border-border">
          {confirm.error instanceof ApiError
            ? confirm.error.message
            : (confirm.error as Error).message}
        </p>
      ) : null}

      <footer className="px-7 py-4 border-t border-border bg-paper flex items-center justify-between gap-2 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm('¿Cancelar esta importación? El PDF se descartará sin crear nada.')) {
              cancel.mutate();
            }
          }}
          disabled={cancel.isPending || confirm.isPending}
        >
          <X className="h-3.5 w-3.5" />
          Cancelar importación
        </Button>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadZip.mutate()}
            disabled={!canConfirm || downloadZip.isPending}
            title="Descarga un ZIP con todos los PDFs recortados como se subirían a cada empleado — sin crear documentos ni notificar."
          >
            <Archive className="h-3.5 w-3.5" />
            {downloadZip.isPending ? 'Preparando ZIP…' : 'Descargar borrador (.zip)'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/documentos')}
            disabled={confirm.isPending}
          >
            Salir sin confirmar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => confirm.mutate()}
            disabled={!canConfirm}
          >
            {confirm.isPending ? 'Confirmando…' : 'Confirmar y crear documentos'}
          </Button>
        </div>
      </footer>
    </div>
  );
}

function DoneStep({ result }: { result: { created: number; skipped: number } }) {
  return (
    <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
      <div className="px-7 py-10 text-center">
        <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
        <h2 className="text-lg font-semibold tracking-tightish">Importación completada</h2>
        <p className="text-[14px] text-muted-foreground mt-2">
          Se crearon <span className="font-mono font-semibold text-foreground">{result.created}</span>{' '}
          documento{result.created === 1 ? '' : 's'} y se descartaron{' '}
          <span className="font-mono font-semibold text-foreground">{result.skipped}</span>{' '}
          página{result.skipped === 1 ? '' : 's'}.
        </p>
        <p className="text-[12.5px] text-muted-foreground mt-1">
          Cada empleado recibirá una notificación con su nómina disponible.
        </p>
        <div className="mt-6 flex gap-2 justify-center">
          <Button variant="primary" asChild>
            <Link to="/admin/documentos">Volver a Documentos</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'center' | 'right' }) {
  return (
    <th
      className={cn(
        'font-mono text-[11px] font-medium uppercase tracking-[0.08em] bg-paper px-5 py-3 text-muted-foreground border-b border-border whitespace-nowrap',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children?: React.ReactNode; align?: 'center' | 'right' }) {
  return (
    <td
      className={cn(
        'px-5 py-3 align-middle',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </td>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
